import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import styled from 'styled-components';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';

// Function to generate random vibrant colors
const getRandomColor = () => {
    // Generate random values for R, G, and B with a minimum threshold for lightness
    const r = Math.floor(Math.random() * 156) + 100; 
    const g = Math.floor(Math.random() * 156) + 100; 
    const b = Math.floor(Math.random() * 156) + 100; 
    return `rgb(${r},${g},${b})`;
};

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  //background-color: #f9f9f9;
  /* background-color: rgb(40, 47, 60); */
  background-color: #17163b;
  border-radius: 2px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  height: auto;
  font-family: 'Crimson Text', sans-serif;
  position: relative; // Ensure Container is positioned relative for tooltip placement
`;

const ChartTitleStyled = styled.h2`
  margin-bottom: 20px;
  font-size: 24px;
  color: white;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 330px;  // Adjust this based on your layout needs
  max-width: 800px; // Adjust to fit your design
  max-height: 600px; // Adjust to fit your design
  position: relative; // Ensure ChartContainer is positioned relative for tooltip placement
`;

// Tooltip styled component
const Tooltip = styled.div`
  position: absolute;
  background: #333;
  color: #fff;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.3s;
  a {
    color: #fff; // Ensure the link color is white
    text-decoration: underline; // Add underline for links
  }
`;

const BubbleChart = ({ data }) => {
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0) {
      console.error('No data provided or data is empty.');
      return;
    }

    const svg = d3.select(svgRef.current);
    const containerWidth = svgRef.current.parentNode.clientWidth;
    const containerHeight = svgRef.current.parentNode.clientHeight || 600;

    // Define margins
    const margin = { top: 20, right: 20, bottom: 60, left: 60 };

    // Adjust width and height to account for margins
    const width = containerWidth - margin.left - margin.right;
    const height = containerHeight - margin.top - margin.bottom;

    svg.attr('width', containerWidth).attr('height', containerHeight);

    // Clear previous contents
    svg.selectAll('*').remove();

    // Append a group element to account for margins
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Process and format data
    const formattedData = data
      .filter(d => d.rank !== "na" && d.et !== "na")
      .map(d => ({
        rank: +d.rank,
        et: +d.et.replace(/,/g, ''),
        title: d.title,
        url: d.url,
        img: d.img,
        da: +d.da
      }))
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 10);

    if (formattedData.length === 0) {
      console.error('No data available to render.');
      return;
    }

    // Define scales
    const minDA = d3.min(formattedData, d => d.da);
    const maxDA = d3.max(formattedData, d => d.da);

    const xScale = d3.scaleLinear()
      .domain([Math.max(0, minDA - 10), Math.min(maxDA + 10, 100)]) // Extend the upper bound to 100
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain([d3.max(formattedData, d => d.rank), 0]) // Start at 0 for y-axis
      .range([height, 0]);

    const radiusScale = d3.scaleSqrt()
      .domain([0, d3.max(formattedData, d => d.et)])
      .range([5, 30]); // Min and max radius

    // Add horizontal grid lines
    g.selectAll('.grid')
      .data(yScale.ticks(10))
      .enter().append('line')
      .attr('class', 'grid')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .style('stroke', 'gray')
      .style('stroke-dasharray', '3,3');

    // Define bubbles
    g.selectAll('circle')
      .data(formattedData)
      .enter().append('circle')
      .attr('cx', d => xScale(d.da))
      .attr('cy', d => yScale(d.rank))
      .attr('r', d => radiusScale(d.et))
      .style('fill', () => getRandomColor()) // Apply random vibrant color
      .style('stroke', '#17163b')
      .style('stroke-width', 1.5)
      .on('mouseover', function(event, d) {
        const tooltip = d3.select(tooltipRef.current);
        tooltip
          .style('opacity', 1)
          .html(`URL: <p>${d.url}</p>`)
          .style('font-size', `17px`)
          .style('font-family', `Crimson Text`);

        // Calculate tooltip position
        const tooltipWidth = tooltip.node().offsetWidth;
        const tooltipHeight = tooltip.node().offsetHeight;
        let left = event.pageX + 10;
        let top = event.pageY - 28;

        // Adjust tooltip position if it overflows the container
        if (left + tooltipWidth > containerWidth) {
          left = containerWidth - tooltipWidth - 10;
        }
        if (top + tooltipHeight > containerHeight) {
          top = containerHeight - tooltipHeight - 10;
        }

        tooltip
          .style('left', `${left}px`)
          .style('top', `${top}px`);
      })
      .on('mousemove', function(event) {
        const tooltip = d3.select(tooltipRef.current);
        const tooltipWidth = tooltip.node().offsetWidth;
        const tooltipHeight = tooltip.node().offsetHeight;
        let left = event.pageX + 10;
        let top = event.pageY - 28;

        // Adjust tooltip position if it overflows the container
        if (left + tooltipWidth > containerWidth) {
          left = containerWidth - tooltipWidth - 10;
        }
        if (top + tooltipHeight > containerHeight) {
          top = containerHeight - tooltipHeight - 10;
        }

        tooltip
          .style('left', `${left}px`)
          .style('top', `${top}px`);
      })
      .on('mouseout', function() {
        d3.select(tooltipRef.current)
          .style('opacity', 0);
      });

    // Add labels
    g.selectAll('text')
      .data(formattedData)
      .enter().append('text')
      .attr('x', d => xScale(d.da))
      .attr('y', d => yScale(d.rank))
      .attr('dy', 4) // Adjust vertical position of text
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', '#000')
      .style('font-family', 'Crimson Text');

    // Add x-axis
    g.append('g')
      .attr('transform', `translate(0,${height})`) // Positioning below the chart
      .call(d3.axisBottom(xScale).tickFormat(d3.format('.0s')))
      .selectAll('text')
      .style('font-family', 'Crimson Text') // Apply font to x-axis ticks
      .style('fill', 'lightgray')
      .style('font-size', '16px');

    g.append('text')
      .attr('x', width - 20)
      .attr('y', height + 40)
      .attr('text-anchor', 'end')
      .style('fill', 'lightgray')
      .text('Domain Authority');

      g.selectAll('.domain') // Axis line
      .style('stroke', 'lightgray'); // Change the axis line color

    // Add y-axis
    g.append('g')
      .call(d3.axisLeft(yScale).ticks(10))
      .selectAll('text')
      .style('font-family', 'Crimson Text') // Apply font to y-axis ticks
      .style('fill', 'lightgray')
      .style('font-size', '16px');

    g.append('text')
      .attr('x', -20)
      .attr('y', -20)
      .attr('text-anchor', 'start')
      .attr('transform', 'rotate(-90)')
      .text('Rank')
      .style('fill', 'lightgray');

      g.selectAll('.domain') // Axis line
      .style('stroke', 'lightgray'); // Change the axis line color

  }, [data]);

  return (
    <Container>
      <ChartTitleStyled><BubbleChartIcon style={{fontSize: 'larger'}}/> Top Sites Analyzed by Backlinks, Domain Authority and Rank</ChartTitleStyled>
      <ChartContainer>
        <svg ref={svgRef} />
        <Tooltip ref={tooltipRef} />
      </ChartContainer>
    </Container>
  );
};

export default BubbleChart;
